/**
 * answerAspectNeed.ts
 *
 * The composer's need-answer write (EAINT-897 C4) — `PUT /schemas/:id/needs/
 * :aspect/:need`. Lives beside `publishSphereType.ts` rather than in
 * `schemaApi.ts`, which is read-only by its own contract.
 *
 * The route's outcomes are the ones a person editing an attachment can
 * actually reach, so they are returned as a discriminated union rather than
 * thrown: `sealed` (409) is not a failure of the call, it is the answer to the
 * question the person asked, and the sheet says so in place. Everything else
 * — 5xx, a network fault — rethrows for the caller's generic error handling,
 * because the composer must never report a need answered that was not.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';

/** Where an answer is recorded: once for the type, or by each sphere of it. */
export type NeedAnswerAt = 'type' | 'record';

export interface AnswerAspectNeedRequest {
  answer: string;
  answeredAt: NeedAnswerAt;
}

export interface AnsweredNeed {
  aspectSystemName: string;
  key: string;
  answer: string;
  at: NeedAnswerAt;
}

export type AnswerAspectNeedOutcome =
  | { outcome: 'answered'; need: AnsweredNeed }
  /** 409 — the schema is sealed; its shape is engine-owned and no answer lands here. */
  | { outcome: 'sealed' }
  /** 403 — the identity lacks `schema#edit`, or an agent tried to defer a need. */
  | { outcome: 'forbidden' }
  /** 404 — the schema is gone, or the aspect is not composed onto it. */
  | { outcome: 'notFound' }
  /** 400 — the need key is unknown for this aspect's collection kind. */
  | { outcome: 'unknownNeed' };

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

const OUTCOME_BY_STATUS: Record<number, AnswerAspectNeedOutcome> = {
  400: { outcome: 'unknownNeed' },
  403: { outcome: 'forbidden' },
  404: { outcome: 'notFound' },
  409: { outcome: 'sealed' },
};

/**
 * Records one answer for one need of one composed aspect.
 *
 * Every path segment is encoded: an aspect system name is author-supplied and
 * a need key comes from the definition, so neither may be pasted into the URL
 * raw.
 */
export async function answerAspectNeed(
  schemaClient: ApiClient,
  schemaId: string,
  aspectSystemName: string,
  needKey: string,
  body: AnswerAspectNeedRequest
): Promise<AnswerAspectNeedOutcome> {
  const url = `/schemas/${encodeURIComponent(schemaId)}/needs/${encodeURIComponent(
    aspectSystemName
  )}/${encodeURIComponent(needKey)}`;
  try {
    const need = await schemaClient.put<AnsweredNeed>(url, body);
    return { outcome: 'answered', need };
  } catch (err) {
    const mapped = OUTCOME_BY_STATUS[statusOf(err) ?? 0];
    if (mapped) return mapped;
    throw err;
  }
}
